// services/routeChargesService.js

import { geocodeLocation } from './distanceService';

// Professional toll database with real toll plazas
const TOLL_DATABASE = {
  // Tamil Nadu Toll Plazas
  'tn': {
    'chennai': [
      { name: 'Chennai Bypass Toll', amount: 45, highway: 'NH16' },
      { name: 'Maduravoyal Toll', amount: 35, highway: 'NH4' },
      { name: 'Porur Toll', amount: 30, highway: 'NH4' }
    ],
    'kanchipuram': [
      { name: 'Kanchipuram Toll Plaza', amount: 55, highway: 'NH48' }
    ],
    'vellore': [
      { name: 'Vellore Toll Plaza', amount: 70, highway: 'NH48' }
    ],
    'sriperumbudur': [
      { name: 'Sriperumbudur Toll', amount: 45, highway: 'NH48' }
    ],
    'kundrathur': [
      { name: 'Kundrathur Bypass', amount: 30, highway: 'NH48' }
    ],
    'ranipet': [
      { name: 'Ranipet Toll', amount: 55, highway: 'NH48' }
    ]
  },
  // Andhra Pradesh Toll Plazas
  'ap': {
    'chittoor': [
      { name: 'Chittoor Toll Plaza', amount: 65, highway: 'NH48' }
    ],
    'tirupati': [
      { name: 'Tirupati Entry Toll', amount: 35, highway: 'NH71' },
      { name: 'Renigunta Toll', amount: 40, highway: 'NH71' }
    ]
  },
  // Karnataka Toll Plazas
  'ka': {
    'bangalore': [
      { name: 'Electronic City Toll', amount: 60, highway: 'NH44' },
      { name: 'Hosur Road Toll', amount: 45, highway: 'NH44' }
    ],
    'hosur': [
      { name: 'Hosur Toll Plaza', amount: 75, highway: 'NH44' }
    ],
    'krishnagiri': [
      { name: 'Krishnagiri Toll', amount: 85, highway: 'NH44' }
    ]
  }
};

// State border tolls
const BORDER_TOLLS = [
  { name: 'Tamil Nadu - Andhra Pradesh Border', state1: 'Tamil Nadu', state2: 'Andhra Pradesh', amount: 120 },
  { name: 'Karnataka - Tamil Nadu Border', state1: 'Karnataka', state2: 'Tamil Nadu', amount: 150 },
  { name: 'Andhra Pradesh - Karnataka Border', state1: 'Andhra Pradesh', state2: 'Karnataka', amount: 130 }
];

// Permit charges by state and vehicle type
const PERMIT_CHARGES = {
  'Tamil Nadu': {
    'Andhra Pradesh': {
      sedan: 550,
      suv: 700,
      innova: 1386,
      description: 'Inter-state permit + state tax'
    },
    'Karnataka': {
      sedan: 600,
      suv: 750,
      innova: 1500,
      description: 'Inter-state permit + state tax'
    },
    'Kerala': {
      sedan: 580,
      suv: 720,
      innova: 1450,
      description: 'Inter-state permit + state tax'
    }
  },
  'Andhra Pradesh': {
    'Tamil Nadu': {
      sedan: 550,
      suv: 700,
      innova: 1386,
      description: 'Inter-state permit + state tax'
    }
  }
};

// Vehicle type mapping
const VEHICLE_TYPE_MAP = {
  4: 'sedan',
  6: 'suv',
  7: 'innova'
};

// Common routes with pre-calculated tolls
const COMMON_ROUTES = {
  'kundrathur-tirupati': {
    tolls: [
      { name: 'Kundrathur Bypass', amount: 30, location: 'Kundrathur' },
      { name: 'Sriperumbudur Toll', amount: 45, location: 'Sriperumbudur' },
      { name: 'Kanchipuram Toll Plaza', amount: 55, location: 'Kanchipuram' },
      { name: 'Chittoor Toll Plaza', amount: 65, location: 'Chittoor' },
      { name: 'Tirupati Entry Toll', amount: 35, location: 'Tirupati' }
    ],
    totalToll: 230,
    distance: 138
  },
  'chennai-bangalore': {
    tolls: [
      { name: 'Chennai Bypass', amount: 45, location: 'Chennai' },
      { name: 'Sriperumbudur Toll', amount: 45, location: 'Sriperumbudur' },
      { name: 'Kanchipuram Toll', amount: 55, location: 'Kanchipuram' },
      { name: 'Vellore Toll', amount: 70, location: 'Vellore' },
      { name: 'Krishnagiri Toll', amount: 85, location: 'Krishnagiri' },
      { name: 'Electronic City Toll', amount: 60, location: 'Bangalore' }
    ],
    totalToll: 360,
    distance: 350
  }
};

// Toll rates per km by highway type
const HIGHWAY_TOLL_RATES = {
  'NH44': 1.8,
  'NH48': 1.6,
  'NH16': 1.4,
  'NH71': 1.5,
  'state-highway': 1.0,
  'expressway': 2.5
};

// ✅ LONG DISTANCE CHARGE CONSTANTS
const LONG_DISTANCE_THRESHOLD_KM = 40;
const LONG_DISTANCE_CHARGE_AMOUNT = 500;

class RouteChargesService {
  constructor() {
    this.cache = new Map();
  }

  // ✅ NEW METHOD: Calculate long distance charge
  calculateLongDistanceCharge(distance, tripType = 'oneway') {
    const numericDistance = Number(distance);
    const tripMultiplier = tripType === 'roundtrip' ? 2 : 1;
    
    if (numericDistance > LONG_DISTANCE_THRESHOLD_KM) {
      const charge = LONG_DISTANCE_CHARGE_AMOUNT * tripMultiplier;
      
      return {
        amount: charge,
        applied: true,
        threshold: LONG_DISTANCE_THRESHOLD_KM,
        distance: numericDistance,
        tripType: tripType,
        multiplier: tripMultiplier,
        breakdown: {
          item: `🚀 Long Distance Charge (Above ${LONG_DISTANCE_THRESHOLD_KM} km)`,
          amount: charge,
          perTripCharge: LONG_DISTANCE_CHARGE_AMOUNT,
          note: `₹${LONG_DISTANCE_CHARGE_AMOUNT} added for ${numericDistance}km trip`
        }
      };
    }
    
    return {
      amount: 0,
      applied: false,
      threshold: LONG_DISTANCE_THRESHOLD_KM,
      distance: numericDistance,
      tripType: tripType,
      multiplier: tripMultiplier,
      breakdown: null
    };
  }

  // Main method to calculate all charges
  async calculateCharges(from, to, vehicleSeaters, distance, tripType = 'oneway') {
    const cacheKey = `${from}-${to}-${vehicleSeaters}-${tripType}`;
    
    if (this.cache.has(cacheKey)) {
      return this.cache.get(cacheKey);
    }

    try {
      // Get states
      const fromState = await this.getStateFromLocation(from);
      const toState = await this.getStateFromLocation(to);
      
      // Check if inter-state
      const isInterState = fromState && toState && fromState !== toState;
      
      // Get vehicle type
      const vehicleType = VEHICLE_TYPE_MAP[vehicleSeaters] || 'sedan';
      
      // Calculate toll charges
      const tollResult = await this.calculateTollCharges(from, to, fromState, toState, distance, vehicleType);
      
      // Calculate permit charges (only if inter-state)
      let permitCharges = 0;
      if (isInterState) {
        permitCharges = this.calculatePermitCharges(fromState, toState, vehicleType);
      }
      
      // ✅ Calculate long distance charge
      const longDistanceCharge = this.calculateLongDistanceCharge(distance, tripType);
      
      // Apply round trip multiplier for tolls
      const tripMultiplier = tripType === 'roundtrip' ? 2 : 1;
      
      const result = {
        tollCharges: tollResult.total * tripMultiplier,
        permitCharges: isInterState ? permitCharges : 0,
        longDistanceCharge: longDistanceCharge.amount,
        totalExtraCharges: (tollResult.total * tripMultiplier) + (isInterState ? permitCharges : 0) + longDistanceCharge.amount,
        isInterState,
        fromState,
        toState,
        tollBreakdown: tripType === 'roundtrip' 
          ? [...tollResult.breakdown, ...tollResult.breakdown.map(t => ({ ...t, name: `${t.name} (Return)` }))]
          : tollResult.breakdown,
        permitDetails: isInterState ? {
          amount: permitCharges,
          description: PERMIT_CHARGES[fromState]?.[toState]?.description || 'Inter-state permit'
        } : null,
        longDistanceDetails: longDistanceCharge.breakdown,
        calculationMethod: tollResult.method
      };
      
      // Cache the result
      this.cache.set(cacheKey, result);
      
      return result;
    } catch (error) {
      console.error('Error calculating charges:', error);
      return this.getFallbackCharges(vehicleSeaters, distance, tripType);
    }
  }

  // Calculate toll charges
  async calculateTollCharges(from, to, fromState, toState, distance, vehicleType) {
    // Check if route exists in common routes
    const routeKey = this.generateRouteKey(from, to);
    if (COMMON_ROUTES[routeKey]) {
      const route = COMMON_ROUTES[routeKey];
      return {
        total: route.totalToll,
        breakdown: route.tolls,
        method: 'exact-route'
      };
    }
    
    // Get coordinates for route analysis
    const fromCoords = await geocodeLocation(from);
    const toCoords = await geocodeLocation(to);
    
    if (!fromCoords || !toCoords) {
      return this.estimateTollByDistance(distance, vehicleType);
    }
    
    // Identify cities along the route
    const routeCities = await this.identifyRouteCities(from, to, fromState, toState);
    
    // Collect tolls from each city
    let totalToll = 0;
    let tollBreakdown = [];
    let processedTolls = new Set();
    
    for (const city of routeCities) {
      const cityTolls = await this.getTollsForCity(city.city, city.state);
      for (const toll of cityTolls) {
        const tollKey = `${toll.name}-${toll.amount}`;
        if (!processedTolls.has(tollKey)) {
          processedTolls.add(tollKey);
          totalToll += toll.amount;
          tollBreakdown.push({
            ...toll,
            location: city.city,
            state: city.state
          });
        }
      }
    }
    
    // Add border toll if inter-state
    if (fromState !== toState) {
      const borderToll = this.getBorderToll(fromState, toState);
      if (borderToll) {
        totalToll += borderToll.amount;
        tollBreakdown.push(borderToll);
      }
    }
    
    return {
      total: totalToll || this.estimateTollByDistance(distance, vehicleType).total,
      breakdown: tollBreakdown.length ? tollBreakdown : this.estimateTollByDistance(distance, vehicleType).breakdown,
      method: totalToll ? 'dynamic' : 'estimated'
    };
  }

  // Get tolls for a specific city
  async getTollsForCity(city, state) {
    const stateCode = this.getStateCode(state);
    const cityKey = city.toLowerCase().replace(/\s+/g, '');
    
    return TOLL_DATABASE[stateCode]?.[cityKey] || [];
  }

  // Calculate permit charges
  calculatePermitCharges(fromState, toState, vehicleType) {
    return PERMIT_CHARGES[fromState]?.[toState]?.[vehicleType] || 
           PERMIT_CHARGES[toState]?.[fromState]?.[vehicleType] || 
           550; // Default permit charge
  }

  // Estimate toll by distance
  estimateTollByDistance(distance, vehicleType) {
    const ratePerKm = {
      sedan: 1.2,
      suv: 1.5,
      innova: 1.6
    }[vehicleType] || 1.2;
    
    const estimatedToll = Math.round(distance * ratePerKm);
    
    return {
      total: estimatedToll,
      breakdown: [
        { name: 'Toll charges (estimated)', amount: estimatedToll, location: 'route' }
      ],
      method: 'distance-based'
    };
  }

  // Get border toll between states
  getBorderToll(state1, state2) {
    return BORDER_TOLLS.find(toll => 
      (toll.state1 === state1 && toll.state2 === state2) ||
      (toll.state1 === state2 && toll.state2 === state1)
    );
  }

  // Identify cities along the route
  async identifyRouteCities(from, to, fromState, toState) {
    // This would use a routing service in production
    // For demo, return common cities for known routes
    if (from.toLowerCase().includes('kundrathur') && to.toLowerCase().includes('tirupati')) {
      return [
        { city: 'Kundrathur', state: 'Tamil Nadu' },
        { city: 'Sriperumbudur', state: 'Tamil Nadu' },
        { city: 'Kanchipuram', state: 'Tamil Nadu' },
        { city: 'Chittoor', state: 'Andhra Pradesh' },
        { city: 'Tirupati', state: 'Andhra Pradesh' }
      ];
    }
    return [];
  }

  // Get state from location
  async getStateFromLocation(location) {
    try {
      const result = await geocodeLocation(location);
      return result?.state || '';
    } catch (error) {
      return '';
    }
  }

  // Get state code
  getStateCode(state) {
    const stateCodes = {
      'Tamil Nadu': 'tn',
      'Andhra Pradesh': 'ap',
      'Karnataka': 'ka',
      'Kerala': 'kl'
    };
    return stateCodes[state] || 'tn';
  }

  // Generate route key
  generateRouteKey(from, to) {
    const fromNorm = from.split(',')[0].trim().toLowerCase().replace(/\s+/g, '');
    const toNorm = to.split(',')[0].trim().toLowerCase().replace(/\s+/g, '');
    return `${fromNorm}-${toNorm}`;
  }

  // Fallback charges
  getFallbackCharges(vehicleSeaters, distance, tripType) {
    const vehicleType = VEHICLE_TYPE_MAP[vehicleSeaters] || 'sedan';
    const baseToll = vehicleType === 'sedan' ? 155 : vehicleType === 'suv' ? 180 : 200;
    const tripMultiplier = tripType === 'roundtrip' ? 2 : 1;
    
    // ✅ Calculate long distance charge for fallback
    const longDistanceCharge = this.calculateLongDistanceCharge(distance, tripType);
    
    return {
      tollCharges: baseToll * tripMultiplier,
      permitCharges: 0,
      longDistanceCharge: longDistanceCharge.amount,
      totalExtraCharges: (baseToll * tripMultiplier) + longDistanceCharge.amount,
      isInterState: false,
      tollBreakdown: [{ name: 'Toll charges', amount: baseToll * tripMultiplier }],
      longDistanceDetails: longDistanceCharge.breakdown,
      calculationMethod: 'fallback'
    };
  }

  // Clear cache
  clearCache() {
    this.cache.clear();
  }
}

export const routeChargesService = new RouteChargesService();